import React from "react";
import SecurityModal from "./SecurityModal";
import Modal from "./Modal";
import SubmitHookForm from "./SubmitHookForm";
import CloseIconButton from "./CloseIconButton";
import CancelButton from "./CancelButton";
import { Controller } from "react-hook-form";
import SelectHookFormSingle from "./SelectHookFormSingle";
import TextInputLabelHookForm from "./TextInputLabelHookForm";
import CheckBoxLabelHookForm from "./CheckBoxLabelHookForm";
import { RequiredInput } from "modules/CarrierAgreementMgmt/Security/atoms/atoms";
import { useRecoilState } from "recoil";

interface SecurityAlarmFormProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "initialAdd" | "add" | "edit" | "initialEdit";
  isSaving?: boolean;
  onSubmit: (data: any) => void;
  formMethods: any;

  vendorsOption: any[];
  dataConnOption: any[];
  alarmTypeOption: any[];
  motionCensorOptions: any[];
  doorContactOptions: any[];
  panicButtonOptions: any[];

  hasDeletePermission?: boolean;
  isEditState?: boolean;
}

const SecurityAlarmForm: React.FC<SecurityAlarmFormProps> = ({
  isOpen,
  onClose,
  mode,
  isSaving = false,
  onSubmit,
  formMethods,
  vendorsOption,
  dataConnOption,
  alarmTypeOption,
  motionCensorOptions,
  doorContactOptions,
  panicButtonOptions,
  hasDeletePermission,
  isEditState,
}) => {
  const ModalComponent =
    mode === "initialAdd" || mode === "add" ? SecurityModal : Modal;
  const getTitle = () => {
    switch (mode) {
      case "initialAdd":
        return "Add Alarm";
      case "add":
        return "Add Alarm";
      case "edit":
        return "Edit Alarm";
      case "initialEdit":
        return "Edit Alarm";
      default:
        return "Alarm Form";
    }
  };

  const getButtonText = () => {
    if (isSaving) return "Saving..";
    switch (mode) {
      case "initialAdd":
        return "Proceed";
      case "add":
        return "Save";
      case "edit":
        return "Save";
      case "initialEdit":
        return "Proceed";
      default:
        return "Submit";
    }
  };

  const getFieldName = (fieldName: string) => {
    return mode === "initialAdd" || mode === "add"
      ? `alarm[0].${fieldName}`
      : fieldName;
  };

  const showCloseIcon =
    mode === "initialAdd" || mode === "add" || mode === "initialEdit";
  const showCancelButton = mode === "edit";
  const [isRequired, setIsRequired] = useRecoilState(RequiredInput);
  const { control } = formMethods;

  return (
    <ModalComponent
      isCloseHide={true}
      modalFilterStat=""
      className="max-w-[1200px]"
      width=""
      title=""
      isOpen={isOpen}
      onClose={undefined}
      hasCloseButton={false}
    >
      {/* Header */}
      <div className="flex flex-row whitespace-nowrap justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
        <div className="ml-2 font-bold text-primary text-md">{getTitle()}</div>

        <div className="flex justify-between gap-2 mr-2">
          <SubmitHookForm
            type="button"
            disabled={false}
            buttonText={getButtonText()}
            className={undefined}
            onClick={onSubmit}
          />
          {showCloseIcon && (
            <div className="relative -top-2 -right-6">
              <CloseIconButton
                onClick={() => {
                  setIsRequired(false);
                  onClose();
                }}
              />
            </div>
          )}

          {showCancelButton && (
            <CancelButton
              onClick={() => {
                setIsRequired(false);
                onClose();
              }}
            />
          )}
        </div>
      </div>

      <div className="w-full border-b-2 border-secondary mb-3"></div>

      {(mode === "initialAdd" || mode === "add") && (
        <div className="text-xs text-secondary italic font-normal m-2">
          <b>Note:</b> All fields with * are required to fill.
        </div>
      )}

      {/* Form Content */}
      <div className="box-shadow rounded-md modal-content">
        <div className="grid grid-cols-1 md:grid-cols-2 mx-4 gap-6 pb-4">
          {/* Left Column */}
          <div className="space-y-1">
            <Controller
              name={getFieldName("vendorId")}
              control={control}
              rules={{ required: "Vendor is Required" }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => {
                    field.onChange(e ? e.value : null);
                    formMethods.setValue(
                      getFieldName("vendorPhone"),
                      e ? e.vendorPhone : null
                    );
                    formMethods.setValue(
                      getFieldName("accountNumber"),
                      e ? e.accountNumber : null
                    );
                  }}
                  value={field.value}
                  options={vendorsOption}
                  label="Vendor Name"
                  className={
                    isRequired ? "border-2 border-red-500 col-span-2" : ""
                  }
                  required={true}
                />
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("vendorPhone")}
              label="Vendor Phone"
              type="text"
              register={formMethods.register(getFieldName("vendorPhone"), {})}
              maxLength={50}
              disabled={true}
            />

            <TextInputLabelHookForm
              name={getFieldName("accountNumber")}
              label="Account Number"
              type="text"
              register={formMethods.register(getFieldName("accountNumber"), {})}
              maxLength={50}
              disabled={true}
            />

            <TextInputLabelHookForm
              name={getFieldName("contractRenewalDate")}
              label="Contract Renewal Date"
              type="date"
              register={formMethods.register(
                getFieldName("contractRenewalDate"),
                {}
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("alarmSiteNumber")}
              label="Alarm Site Number"
              type="text"
              register={formMethods.register(
                getFieldName("alarmSiteNumber"),
                {}
              )}
              maxLength={100}
            />

            <Controller
              name={getFieldName("dataConnectionId")}
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <div className="w-full">
                  <div className="sm:w-full md:w-full lg:w-full gap-y-4">
                    <SelectHookFormSingle
                      field={field}
                      onChange={(e) => field.onChange(e ? e.value : null)}
                      value={field.value}
                      options={dataConnOption}
                      label="Data Connection"
                      className={
                        formMethods.formState.errors.dataConnectionId &&
                        "border-2 border-red-500 col-span-2"
                      }
                      required={false}
                    />
                  </div>
                </div>
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("alarmInstallDate")}
              label="Install Date"
              type="date"
              register={formMethods.register(
                getFieldName("alarmInstallDate"),
                {}
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("ipaddress")}
              label="IP Address"
              type="text"
              register={formMethods.register(getFieldName("ipaddress"), {})}
              maxLength={100}
            />

            <TextInputLabelHookForm
              name={getFieldName("ipGateway")}
              label="IP Gateway"
              type="text"
              register={formMethods.register(getFieldName("ipGateway"), {})}
              maxLength={100}
            />

            <TextInputLabelHookForm
              name={getFieldName("subnetMask")}
              label="Subnet Mask"
              type="text"
              register={formMethods.register(getFieldName("subnetMask"), {})}
              maxLength={100}
            />
          </div>

          {/* Right Column */}
          <div
            className={`space-y-1 ${
              mode === "initialAdd" || mode === "add" ? "mr-4" : ""
            }`}
          >
            <TextInputLabelHookForm
              name={getFieldName("dns")}
              label="DNS"
              type="text"
              register={formMethods.register(getFieldName("dns"), {})}
              maxLength={100}
            />

            <TextInputLabelHookForm
              name={getFieldName("connectionDetails")}
              label="Connection Details"
              type="text"
              register={formMethods.register(
                getFieldName("connectionDetails"),
                {}
              )}
              maxLength={100}
            />

            <Controller
              name={getFieldName("alarmTypeId")}
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e ? e.value : null)}
                  value={field.value}
                  options={alarmTypeOption}
                  label="Alarm Type"
                  className={
                    formMethods.formState.errors.alarmTypeId &&
                    "border-2 border-red-500 col-span-2"
                  }
                  required={false}
                />
              )}
            />

            <Controller
              name={getFieldName("numberOfMotionSensors")}
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e ? e.value : null)}
                  value={field.value}
                  options={motionCensorOptions}
                  label="Number Of Motion Sensors"
                  className={
                    formMethods.formState.errors.numberOfMotionSensors &&
                    "border-2 border-red-500 col-span-2"
                  }
                  required={false}
                />
              )}
            />

            <Controller
              name={getFieldName("numberOfDoorContacts")}
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e ? e.value : null)}
                  value={field.value}
                  options={doorContactOptions}
                  label="Number Of Door Contact"
                  className={
                    formMethods.formState.errors.numberOfDoorContacts &&
                    "border-2 border-red-500 col-span-2"
                  }
                  required={false}
                />
              )}
            />

            <Controller
              name={getFieldName("numberOfPanicButtons")}
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e ? e.value : null)}
                  value={field.value}
                  options={panicButtonOptions}
                  label="Number Of Panic Buttons"
                  className={
                    formMethods.formState.errors.numberOfPanicButtons &&
                    "border-2 border-red-500 col-span-2"
                  }
                  required={false}
                />
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("specialEquipment")}
              label="Any Additional Special Equipment"
              type="text"
              register={formMethods.register(
                getFieldName("specialEquipment"),
                {}
              )}
              maxLength={150}
            />

            <TextInputLabelHookForm
              name={getFieldName("cancellationDate")}
              label="Cancellation Date"
              type="date"
              register={formMethods.register(
                getFieldName("cancellationDate"),
                {}
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("portNumber")}
              label="Port Number"
              type="text"
              register={formMethods.register(getFieldName("portNumber"), {})}
              maxLength={100}
            />

            {/* only active if edit*/}
            {mode === "edit" && isEditState && hasDeletePermission && (
              <CheckBoxLabelHookForm
                name="isActive"
                type="checkbox"
                register={formMethods.register}
                label="Active Account"
                className="cursor-pointer auto-rows-auto text-right justify-items-center"
              />
            )}
          </div>
        </div>

        {(mode === "initialAdd" || mode === "add") && (
          <div>
            <div className="w-full border-b-2 border-grey-200"></div>
          </div>
        )}
      </div>
    </ModalComponent>
  );
};

export default SecurityAlarmForm;
